// ConfettiCelebration.jsx
import React, { useRef, useEffect, useCallback } from "react";

/**
 * <ConfettiCelebration trigger={boolean} onDone={() => {}} />
 *
 * - trigger: when it changes from false -> true the animation starts
 * - onDone: optional callback when confetti finishes
 *
 * This component is "vanilla" (no dependencies), uses a canvas and requestAnimationFrame.
 */

const COLORS = [
  "#FFC700", // yellow
  "#FF0000", // red
  "#2E3192", // indigo
  "#00ADEF", // blue
  "#00A86B", // green
  "#FF6F61", // coral
  "#8A2BE2", // purple
];

function random(min, max) {
  return Math.random() * (max - min) + min;
}

function createParticle(x, y, canvasWidth) {
  // a single confetti particle
  const size = random(6, 14);
  const tilt = random(-15, 15);
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];
  const velocity = {
    x: random(-6, 6) + (x < canvasWidth / 2 ? 1 : -1), // push outward
    y: random(-12, -6),
  };
  const rotation = random(0, Math.PI * 2);
  const angularVel = random(-0.2, 0.2);
  const lifetime = random(60, 120); // frames
  return { x, y, size, tilt, color, velocity, rotation, angularVel, life: 0, lifetime };
}

export default function ConfettiCelebration({ trigger = false, onDone = () => {} , zIndex = 9999 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);
  const startedRef = useRef(false);
  const lastTriggerRef = useRef(false);
  const resizeObserverRef = useRef(null);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    return ctx;
  }, []);

  const spawnBurst = useCallback((count = 80) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 3;
    for (let i = 0; i < count; i++) {
      particlesRef.current.push(createParticle(cx + random(-rect.width/4, rect.width/4), cy + random(-30, 30), rect.width));
    }
  }, []);

  const step = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);

    const particles = particlesRef.current;
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];

      // physics
      p.velocity.y += 0.45 * (p.size / 12); // gravity scaled by size
      p.x += p.velocity.x;
      p.y += p.velocity.y;
      p.rotation += p.angularVel;
      p.tilt = Math.sin((p.life / p.lifetime) * Math.PI * 4 + p.rotation) * 12;

      // draw as small rotated rectangle
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      // use tilt to emulate fluttering — draw rect with tilt offset
      ctx.fillRect(-p.size/2 + p.tilt/6, -p.size/2, p.size, p.size * 0.6);
      ctx.restore();

      p.life++;
      // remove if out of bounds or life ended
      if (p.life > p.lifetime || p.y - p.size > rect.height + 50) {
        particles.splice(i, 1);
      }
    }

    // continue if we still have particles or the animation was started recently
    if (particles.length > 0) {
      rafRef.current = requestAnimationFrame(step);
    } else {
      startedRef.current = false;
      rafRef.current = null;
      onDone && onDone();
    }
  }, [onDone]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // initialize canvas and observer to handle container size changes
    initCanvas();
    const ro = new ResizeObserver(() => {
      initCanvas();
    });
    ro.observe(canvas);
    resizeObserverRef.current = ro;
    return () => {
      ro.disconnect();
      // cancel any rAF on unmount
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [initCanvas]);

  // trigger watcher: start when trigger changes from false -> true
  useEffect(() => {
    const justToggled = trigger && !lastTriggerRef.current;
    lastTriggerRef.current = trigger;
    if (justToggled && !startedRef.current) {
      startedRef.current = true;
      // spawn multiple bursts for a nicer effect
      spawnBurst(90);
      // small delayed extra bursts
      setTimeout(() => spawnBurst(40), 120);
      setTimeout(() => spawnBurst(30), 260);
      if (!rafRef.current) rafRef.current = requestAnimationFrame(step);
    }
  }, [trigger, spawnBurst, step]);

  // expose a click-to-fire behavior if someone clicks canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // spawn small burst at click point
      for (let i = 0; i < 40; i++) {
        particlesRef.current.push(createParticle(x + random(-30,30), y + random(-10,10), rect.width));
      }
      if (!rafRef.current) rafRef.current = requestAnimationFrame(step);
    };
    canvas.addEventListener("click", onClick);
    return () => canvas.removeEventListener("click", onClick);
  }, [step]);

  if(!trigger) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "auto", // allows clicks to spawn bursts if desired
        zIndex,
      }}
    />
  );
}
